import type { NavigateFunction } from "react-router";
import { match } from "ts-pattern";

import { dayjs } from "@thalamos/common";
import type {
  ExtendedFormDraft,
  ExtendedThalamosUser,
  FormContextData,
} from "@thalamos/aspire-common";
import {
  COMPLETED_FORM_STATUSES,
  currentVersionRestricted,
  getBaseFormTemplate,
  getFullFormName,
  isSoadUserSession,
} from "@thalamos/aspire-common";

import { api } from "~/api.js";
import { BannerList } from "~/components/design-system/index.js";
import { computeUserAndWorkItemState } from "~/pages/FormProgressPage/helpers/formContextLogic.js";
import { routeFns } from "~/routePaths.js";

export type SectionName =
  | "patientDetails"
  | "assessmentDetails"
  | "diagnosisAndHistory"
  | "currentAndProposedTreatment"
  | "consulteeDetails"
  | "soadAuthorisation"
  | "mha-soad-t2"
  | "mha-soad-t3"
  | "mha-soad-t4"
  | "mha-soad-t5"
  | "mha-soad-t6"
  | "mha-soad-cto11"
  | "mha-soad-nocert";

export function completionStatusToStatus(
  completionStatus: "completed" | "in-progress" | "not-started",
  isAmendInProgress?: boolean,
) {
  switch (completionStatus) {
    case "completed":
      return "completed";
    case "in-progress":
      return "in-progress";
    case "not-started":
      return "not-started";
    default:
      return isAmendInProgress ? "parked" : "not-started";
  }
}

type SoadReportFormSectionInfo = {
  state: "completed" | "in-progress" | "not-started" | "parked";
  title: string;
  banner?: {
    type: "info" | "warning" | "error";
    bannerText: string;
  };
  onClick: () => void;
  sectionName: SectionName;
  subHeading?: string;
  draft?: ExtendedFormDraft;
};

export function soadAssessmentLogic(
  formContext: FormContextData,
  user: ExtendedThalamosUser,
  navigateFn: NavigateFunction,
  hasActiveWorkItem: boolean,
) {
  const { myActiveWorkItem, amMha } = computeUserAndWorkItemState(
    user,
    formContext.activeTeamworkWorkItem,
  );

  const isSoad = isSoadUserSession(user);
  const soadReportForm = formContext.forms.find(
    (form) => form.formTemplate.id === "soad-report-form",
  );
  const soadReportFormTemplate = soadReportForm
    ? getBaseFormTemplate(soadReportForm.formTemplate)
    : undefined;

  const fullFormName = soadReportFormTemplate
    ? getFullFormName(soadReportFormTemplate)
    : "";

  const soadAssessmentComplete = match(soadReportForm?.status)
    .with("complete", "finalised", () => true)
    .with(undefined, "in-progress", "deleted", () => false)
    .exhaustive();

  const amended = (soadReportForm?.latestVersion ?? 1) > 1;

  const amendInProgress =
    soadReportForm?.status === "in-progress" &&
    soadReportForm.latestVersion > 1;

  const canCancelAmend = amendInProgress && isSoad && hasActiveWorkItem;

  const soadReportFormLatestVersionDrafts = soadReportForm?.versions.find(
    (version) => version.version === soadReportForm.latestVersion,
  );

  const signatureCreatedAt =
    soadReportFormLatestVersionDrafts?.signatures[0]?.created;

  const isWithin28Days = signatureCreatedAt
    ? dayjs().isBefore(dayjs(signatureCreatedAt).add(28, "day"))
    : false;

  const canAmend =
    soadReportForm?.status === "complete" &&
    isSoad &&
    hasActiveWorkItem &&
    isWithin28Days;

  // We require at least one participant who is not a SOAD or CQC user to be added to the form.
  const formNeedsProviderParticipant = !formContext.workItems
    .filter((workItem) =>
      match(workItem.status)
        .with("pending", "accepted", "merged", "completed", () => true)
        .with("rejected", "cancelled", () => false)
        .exhaustive(),
    )
    .some((workItem) =>
      match(workItem.team.type)
        .with("cqc", "soad", () => false)
        .with(
          "amhp",
          "mha",
          "mca",
          "ward",
          "independent",
          "guest",
          "generic",
          "section-12-payment-reviewers",
          () => true,
        )
        .exhaustive(),
    );

  // The form requires CQC
  const formNeedsCqcParticipant = !formContext.workItems
    .filter((workItem) =>
      match(workItem.status)
        .with("pending", "accepted", "merged", "completed", () => true)
        .with("rejected", "cancelled", () => false)
        .exhaustive(),
    )
    .some((workItem) =>
      match(workItem.team.type)
        .with("cqc", () => true)
        .with(
          "amhp",
          "mha",
          "mca",
          "ward",
          "independent",
          "guest",
          "generic",
          "soad",
          "section-12-payment-reviewers",
          () => false,
        )
        .exhaustive(),
    );

  const formNeedsFinalise =
    soadReportForm?.status === "complete" || amendInProgress;
  const canFinaliseForm = formNeedsFinalise && !!myActiveWorkItem && amMha;

  const assessmentDetailsDraft = soadReportForm?.allDrafts?.find(
    (draft) => draft.part === 1,
  );
  const diagnosisAndHistoryDraft = soadReportForm?.allDrafts?.find(
    (draft) => draft.part === 2,
  );
  const currentAndProposedTreatmentDraft = soadReportForm?.allDrafts?.find(
    (draft) => draft.part === 3,
  );
  const consulteeDetailsDraft = soadReportForm?.allDrafts?.find(
    (draft) => draft.part === 4,
  );
  const soadAuthorisationDraft = soadReportForm?.allDrafts?.find(
    (draft) => draft.part === 5,
  );

  const soadNotesDraft = soadReportForm?.allDrafts?.find(
    (draft) => draft.part === 6,
  );

  const allSrfSectionsMarkedComplete =
    assessmentDetailsDraft?.data.completionStatus === "completed" &&
    diagnosisAndHistoryDraft?.data.completionStatus === "completed" &&
    currentAndProposedTreatmentDraft?.data.completionStatus === "completed" &&
    consulteeDetailsDraft?.data.completionStatus === "completed" &&
    soadAuthorisationDraft?.data.completionStatus === "completed";

  const canViewSoadReportPdf =
    (soadReportForm?.status &&
      COMPLETED_FORM_STATUSES.includes(soadReportForm.status)) ||
    allSrfSectionsMarkedComplete ||
    amendInProgress;

  const canDownloadSoadReportPdf =
    (soadReportForm?.status &&
      COMPLETED_FORM_STATUSES.includes(soadReportForm.status)) ||
    (amendInProgress && !isSoad);

  const certificates = formContext.forms
    .filter((f) => f.formTemplate.id !== "soad-report-form")
    .filter((f) =>
      amendInProgress
        ? f.allDrafts && f.allDrafts?.length > 0
        : f.status !== "deleted" &&
          !(f.status === "finalised" && f.preFinalisedFormStatus === "deleted"),
    )
    .sort((a, b) => {
      // "mha-soad-t2" → "t2"; a template id with no third segment sorts first.
      const aId = a.formTemplate.id.split("-")[2] ?? "";
      const bId = b.formTemplate.id.split("-")[2] ?? "";
      return aId.localeCompare(bId);
    });

  const deletedCertificates = formContext.forms
    .filter((f) => f.formTemplate.id !== "soad-report-form")
    .filter(
      (f) =>
        f.status === "deleted" ||
        (f.status === "finalised" && f.preFinalisedFormStatus === "deleted"),
    );

  const allCertificatesReadyToSign = certificates.every(
    (c) => c.allDrafts?.[0]?.data.completionStatus === "completed",
  );

  const allFormsReadyToSign =
    allCertificatesReadyToSign && allSrfSectionsMarkedComplete;

  const correctNumberOfCertificates =
    certificates.length >= 1 && certificates.length <= 6;

  const canReview = allFormsReadyToSign && correctNumberOfCertificates;

  const canUploadFormToExternalSystem =
    user.sessionContext?.teamType === "mha" &&
    (soadAssessmentComplete || amendInProgress);

  return {
    fullFormName,
    soadAssessmentComplete,
    saveAction: soadAssessmentComplete ? "send" : "review",
    canSave: canReview,
    amended,
    canAmend,
    amendInProgress,
    canCancelAmend,
    formNeedsProviderParticipant,
    formNeedsCqcParticipant,
    canFinaliseForm,
    deletedCertificates,
    allSrfSectionsMarkedComplete,
    canUploadFormToExternalSystem,
    certificates: {
      forms: certificates.map((f) => {
        return {
          form: f,
          title: `pages.soadAssessment.certificateTitles.${f.formTemplate.id.split("-")[2]}`,
          state: completionStatusToStatus(
            f.allDrafts?.[0]?.data.completionStatus,
            amendInProgress,
          ),
          sectionName: f.formTemplate.id as
            | "mha-soad-t2"
            | "mha-soad-t3"
            | "mha-soad-t4"
            | "mha-soad-t5"
            | "mha-soad-t6"
            | "mha-soad-cto11"
            | "mha-soad-nocert",
          draft: f.allDrafts?.[0],
          restrictedDisclosure: currentVersionRestricted(f),
          onClick: async () => {
            if (amendInProgress && f.status === "complete") {
              await api.soad.addStatutoryCertificate(
                formContext.id,
                f.formTemplate.id,
              );
            }
            navigateFn(
              routeFns.soadReportFormDraftCompletionPage(
                formContext.id,
                formContext.patient.id,
                f.formTemplate.id as
                  | "mha-soad-t2"
                  | "mha-soad-t3"
                  | "mha-soad-t4"
                  | "mha-soad-t5"
                  | "mha-soad-t6"
                  | "mha-soad-cto11"
                  | "mha-soad-nocert",
              ),
            );
          },
        };
      }) satisfies SoadReportFormSectionInfo[],
    },
    soadNotes: {
      draft: soadNotesDraft,
    },
    soadReportForm: {
      form: soadReportForm,
      canViewPdf: canViewSoadReportPdf,
      canDownloadPdf: canDownloadSoadReportPdf,
      editable:
        soadReportForm?.status === "in-progress" && isSoad && hasActiveWorkItem,

      sections: [
        {
          sectionName: "patientDetails",
          state: "completed" as const,
          title: "pages.soadAssessment.srf.patientDetails",
          onClick: () => {
            navigateFn(
              routeFns.patientEdit(
                formContext.patient.id,
                routeFns.formContextPage(
                  formContext.id,
                  formContext.patient.id,
                ),
              ),
            );
          },
        },
        {
          sectionName: "assessmentDetails",
          draft: assessmentDetailsDraft,
          state: completionStatusToStatus(
            assessmentDetailsDraft?.data.completionStatus,
          ),
          title: "pages.soadAssessment.srf.assessmentDetails",
          subHeading: "Summary of Assessment(s)",
          banner: {
            type: BannerList.INFO,
            // AD-1.0
            bannerText:
              "On this page, you can add multiple assessments where relevant. Each entry should represent a distinct in-person visit or remote assessment conducted as part of the overall assessment process for the patient. Please ensure the first entry reflects the initial assessment you carried out.",
          },
          onClick: () => {
            navigateFn(
              routeFns.soadReportFormDraftCompletionPage(
                formContext.id,
                formContext.patient.id,
                "assessmentDetails",
              ),
            );
          },
        },
        {
          sectionName: "diagnosisAndHistory",
          draft: diagnosisAndHistoryDraft,
          state: completionStatusToStatus(
            diagnosisAndHistoryDraft?.data.completionStatus,
          ),
          title: "pages.soadAssessment.srf.diagnosisAndHistory",
          onClick: () => {
            navigateFn(
              routeFns.soadReportFormDraftCompletionPage(
                formContext.id,
                formContext.patient.id,
                "diagnosisAndHistory",
              ),
            );
          },
        },
        {
          sectionName: "currentAndProposedTreatment",
          draft: currentAndProposedTreatmentDraft,
          state: completionStatusToStatus(
            currentAndProposedTreatmentDraft?.data.completionStatus,
          ),
          title: "pages.soadAssessment.srf.currentAndProposedTreatment",
          onClick: () => {
            navigateFn(
              routeFns.soadReportFormDraftCompletionPage(
                formContext.id,
                formContext.patient.id,
                "currentAndProposedTreatment",
              ),
            );
          },
        },
        {
          sectionName: "consulteeDetails",
          draft: consulteeDetailsDraft,
          state: completionStatusToStatus(
            consulteeDetailsDraft?.data.completionStatus,
          ),
          title: "pages.soadAssessment.srf.consulteeDetails",
          onClick: () => {
            navigateFn(
              routeFns.soadReportFormDraftCompletionPage(
                formContext.id,
                formContext.patient.id,
                "consulteeDetails",
              ),
            );
          },
        },
        {
          sectionName: "soadAuthorisation",
          draft: soadAuthorisationDraft,
          state: completionStatusToStatus(
            soadAuthorisationDraft?.data.completionStatus,
          ),
          title: "pages.soadAssessment.srf.soadAuthorisation",
          onClick: () => {
            navigateFn(
              routeFns.soadReportFormDraftCompletionPage(
                formContext.id,
                formContext.patient.id,
                "soadAuthorisation",
              ),
            );
          },
        },
      ] satisfies SoadReportFormSectionInfo[],
    },
  };
}
